import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { UdvegaLogo } from '../components/UdvegaLogo';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

export default function JoinHr() {
  const { acceptHrInvite, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ name: '', password: '' });

  useEffect(() => {
    if (user && user.email) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) {
        setError('This invite link is missing a token.');
        setLoading(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/join-hr`, { params: { token } });
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'This invite link is invalid or has expired.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.password) {
      setError('Name and password are required.');
      return;
    }
    setSubmitting(true);
    const result = await acceptHrInvite(token, form.name.trim(), form.password);
    setSubmitting(false);
    if (result.success) {
      navigate('/dashboard');
      return;
    }
    setError(result.error);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center pt-16 pb-16 px-4"
      style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #134E4A 100%)' }}
      data-testid="join-hr-page"
    >
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="glass-card rounded-3xl p-8 md:p-10 bg-white/80">
          <div className="flex justify-center mb-8">
            <UdvegaLogo size="lg" />
          </div>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : preview ? (
            <>
              <h2 className="font-heading text-2xl font-medium text-slate-800 text-center mb-1">
                Join {preview.company_name}
              </h2>
              <p className="text-sm text-slate-500 text-center mb-8">
                <span className="font-medium text-slate-700">{preview.inviter_name}</span> invited you
                to join this company on Udvega. You will use the same dashboard.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Work email</label>
                  <Input value={preview.email} readOnly className="border-slate-200 bg-slate-50" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Full name</label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="Min 8 characters"
                      minLength={8}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {error && <p className="text-sm text-red-500">{error}</p>}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white h-11"
                >
                  {submitting ? 'Joining...' : 'Join company'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="font-heading text-2xl font-medium text-slate-800 text-center mb-2">
                Invite unavailable
              </h2>
              <p className="text-sm text-slate-500 text-center mb-6">{error}</p>
              <Link to="/login" className="block text-center text-sm text-teal-600 hover:text-teal-700 font-medium">
                Go to sign in
              </Link>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
